import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Timer } from "lucide-react";
import { useLiveMeta } from "@/lib/live";
import { EASE, scrollToId } from "@/lib/scroll";

const STRIP_HEIGHT = "32px";
const pad = (n) => String(n).padStart(2, "0");

/** ms -> {d, h, m, s}; null once the deadline has passed. */
const remaining = (endsAt) => {
  const diff = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(diff) || diff <= 0) return null;
  const s = Math.floor(diff / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};

const Countdown = ({ endsAt, onEnd }) => {
  const [left, setLeft] = useState(() => remaining(endsAt));
  useEffect(() => {
    const id = setInterval(() => {
      const next = remaining(endsAt);
      setLeft(next);
      if (!next) {
        clearInterval(id);
        onEnd();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [endsAt, onEnd]);
  if (!left) return null;
  return (
    <span data-testid="sale-countdown" className="tabular-nums">
      <span className="hidden sm:inline">Ends in </span>
      {left.d > 0 && <span>{left.d}d </span>}
      {pad(left.h)}h {pad(left.m)}m {pad(left.s)}s
    </span>
  );
};

/** Slim strip under the announcement bar while Shopify reports a live automatic discount. The name and % come
 * from the live price probe; the deadline is SALE_END_AT on the backend (Shopify does not publish it).
 * Publishes its height as --sale-strip so the hero can make room. */
export const SaleStrip = () => {
  const { sale } = useLiveMeta();
  const [ended, setEnded] = useState(false);
  // A deadline already in the past (operator forgot to update SALE_END_AT) is treated as "no deadline": the strip
  // still announces the live discount, just without a countdown.
  const deadline = sale?.ends_at && remaining(sale.ends_at) ? sale.ends_at : null;
  const show = Boolean(sale?.title && sale.percent > 0 && !ended);

  useEffect(() => {
    document.documentElement.style.setProperty("--sale-strip", show ? STRIP_HEIGHT : "0px");
    return () => document.documentElement.style.setProperty("--sale-strip", "0px");
  }, [show]);

  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          key="sale-strip"
          data-testid="sale-strip"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: STRIP_HEIGHT, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="relative overflow-hidden border-b border-champagne/20 bg-[#1a1512] text-cream"
        >
          <div aria-hidden className="sale-shimmer pointer-events-none absolute inset-0" />
          <div className="relative mx-auto flex h-8 max-w-[1440px] items-center justify-between gap-3 px-4 font-mono text-[11px] uppercase tracking-[0.16em] sm:px-8 sm:text-xs lg:px-12">
            <p className="flex min-w-0 items-center gap-2 truncate">
              <Timer className="h-3.5 w-3.5 shrink-0 text-champagne" />
              <span data-testid="sale-strip-title" className="truncate">
                <span className="text-champagne">{sale.label || sale.title}</span> · {sale.percent}% off{deadline ? "" : " · live now"}
              </span>
            </p>
            <div className="flex shrink-0 items-center gap-4">
              {deadline && <Countdown endsAt={deadline} onEnd={() => setEnded(true)} />}
              <button data-testid="sale-strip-cta" onClick={() => scrollToId("bestsellers")} className="hidden items-center gap-1 text-champagne transition-colors hover:text-cream sm:inline-flex">
                Shop the sale <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
